import { useApiQuery } from '@/api/main/query'
import { PaginatedCollection } from '@/api/types'
import { Roles } from '@/entities/roles'
import { USER_ROLE_KEY } from '@/features/auth'
import { DEFAULT_TAKE } from '@/shared/constants/constants'
import { getStorageItem } from '@/shared/lib/storage/localStorage'
import { topupsAPI } from '../api'
import { TopupsModel, TopupsModelMerchant, TopupStatus, TopupTraderStatus } from '../model'
import { useDebounce } from '@/shared/hooks'
import { useMemo } from 'react'

type UseTopupsQueryParams = {
  id?: string | undefined
  skip: number
  take?: number
  search?: string | undefined
  startDate?: string
  endDate?: string
  status?: TopupTraderStatus | TopupStatus
  bank?: string | undefined
  method?: string | undefined
  amountFrom?: string | undefined
  amountTo?: string | undefined
  refreshTrigger?: number
}

export function useTopupsQuery({
  id,
  skip,
  take = DEFAULT_TAKE,
  search,
  startDate,
  endDate,
  status,
  amountFrom,
  amountTo,
  bank,
  method,
  refreshTrigger = 0,
}: UseTopupsQueryParams) {
  const debouncedSearch = useDebounce(search, 500)
  const userRole = getStorageItem<Roles | null>(USER_ROLE_KEY, null)
  const isMerchant = userRole === Roles.Merchant

  const queryKey = useMemo(
    () => [
      'topups',
      userRole,
      skip,
      take,
      debouncedSearch,
      status,
      startDate,
      endDate,
      amountFrom,
      amountTo,
      bank,
      method,
      refreshTrigger,
      id,
    ],
    [
      id,
      userRole,
      skip,
      take,
      debouncedSearch,
      status,
      startDate,
      endDate,
      amountFrom,
      amountTo,
      bank,
      method,
      refreshTrigger,
    ]
  )

  const queryParams = useMemo(
    () => ({
      id,
      skip,
      take,
      status,
      paymentRequisit: isMerchant ? search : debouncedSearch,
      ...(isMerchant && {
        startDate,
        endDate,
      }),
    }),
    [id, skip, debouncedSearch, isMerchant, startDate, endDate, status]
  )

  const query = useApiQuery<PaginatedCollection<TopupsModel[] | TopupsModelMerchant[]>>(
    queryKey,
    topupsAPI.list(),
    queryParams
  )

  return {
    ...query,
    isMerchant,
    data: query.data as PaginatedCollection<TopupsModel[] | TopupsModelMerchant[]>,
    queryKey,
    userRole,
  }
}
